import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { CreditCard, Check, Eye, RefreshCw, X, Undo2, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { formatArmenianDate } from '@/utils/dateFormatting';

interface StudentTuitionData {
  id: string;
  studentName: string;
  studentId: string;
  groupName: string;
  groupId: string;
  phaseName: string;
  phaseNumber: number;
  tuitionAmount: number;
  status: string;
  paymentDate?: string;
  createdAt: string;
}

export const StudentTuitionSection = () => {
  const { toast } = useToast();
  const [tuitionData, setTuitionData] = useState<StudentTuitionData[]>([]);
  const [allTuitionData, setAllTuitionData] = useState<StudentTuitionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [payingStudent, setPayingStudent] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedDialogData, setSelectedDialogData] = useState<StudentTuitionData | null>(null);

  const fetchStudentTuitionData = async () => {
    try {
      setLoading(true);

      // Fetch financial records separately
      const { data: financialRecords, error: financialError } = await supabase
        .from('financial_records')
        .select(`
          id,
          user_id,
          amount,
          type,
          description,
          phase_id,
          group_id,
          status,
          approved_at,
          created_at
        `)
        .eq('type', 'tuition_debt')
        .order('created_at', { ascending: true });

      if (financialError) throw financialError;

      if (!financialRecords || financialRecords.length === 0) {
        setTuitionData([]);
        return;
      }

      // Get unique user_ids, group_ids, and phase_ids
      const userIds = [...new Set(financialRecords.map(r => r.user_id))];
      const groupIds = [...new Set(financialRecords.map(r => r.group_id))];
      const phaseIds = [...new Set(financialRecords.map(r => r.phase_id).filter(Boolean))];

      // Fetch profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('id, first_name, last_name')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Fetch groups
      const { data: groups, error: groupsError } = await supabase
        .from('groups')
        .select('id, name')
        .in('id', groupIds);

      if (groupsError) throw groupsError;

      // Fetch phases for all groups to properly handle missing phase_id cases
      let phases: any[] = [];
      if (groupIds.length > 0) {
        const { data: phasesData, error: phasesError } = await supabase
          .from('phases')
          .select('id, name, phase_number, group_id')
          .in('group_id', groupIds)
          .order('group_id', { ascending: true })
          .order('phase_number', { ascending: true });

        if (phasesError) {
          console.error('Error fetching phases:', phasesError);
        } else {
          phases = phasesData || [];
          console.log('Successfully fetched phases:', phases.length);
        }
      }

      // Also fetch phases by their IDs from financial records to ensure we get all referenced phases
      if (phaseIds.length > 0) {
        const { data: additionalPhases, error: additionalPhasesError } = await supabase
          .from('phases')
          .select('id, name, phase_number, group_id')
          .in('id', phaseIds);

        if (!additionalPhasesError && additionalPhases) {
          // Merge additional phases, avoiding duplicates
          const existingPhaseIds = new Set(phases.map(p => p.id));
          additionalPhases.forEach(phase => {
            if (!existingPhaseIds.has(phase.id)) {
              phases.push(phase);
            }
          });
          console.log('Total phases after merging:', phases.length);
        }
      }

      // Fetch first lesson dates for each phase
      const firstLessonDates = new Map();
      if (phaseIds.length > 0) {
        const { data: lessons, error: lessonsError } = await supabase
          .from('lessons')
          .select('phase_id, lesson_date, lesson_number')
          .in('phase_id', phaseIds)
          .eq('lesson_number', 1)
          .order('lesson_date', { ascending: true });

        if (!lessonsError && lessons) {
          lessons.forEach(lesson => {
            if (lesson.phase_id && lesson.lesson_date) {
              firstLessonDates.set(lesson.phase_id, lesson.lesson_date);
            }
          });
        }
      }

      // Also get first lesson dates for groups without phases
      const groupFirstLessonDates = new Map();
      if (groupIds.length > 0) {
        const { data: groupLessons, error: groupLessonsError } = await supabase
          .from('lessons')
          .select('group_id, lesson_date, lesson_number')
          .in('group_id', groupIds)
          .eq('lesson_number', 1)
          .is('phase_id', null)
          .order('lesson_date', { ascending: true });

        if (!groupLessonsError && groupLessons) {
          groupLessons.forEach(lesson => {
            if (lesson.group_id && lesson.lesson_date) {
              if (!groupFirstLessonDates.has(lesson.group_id) || 
                  lesson.lesson_date < groupFirstLessonDates.get(lesson.group_id)) {
                groupFirstLessonDates.set(lesson.group_id, lesson.lesson_date);
              }
            }
          });
        }
      }

      // Create lookup maps
      const profileMap = new Map(profiles?.map(p => [p.id, p]) || []);
      const groupMap = new Map(groups?.map(g => [g.id, g]) || []);
      const phaseMap = new Map(phases.map(p => [p.id, p]));

      // Transform the data
      const tuitionRecords: StudentTuitionData[] = financialRecords?.map(record => {
        const profile = profileMap.get(record.user_id);
        const group = groupMap.get(record.group_id);
        const phase = record.phase_id ? phaseMap.get(record.phase_id) : null;
        
        // Get the appropriate first lesson date
        let firstLessonDate = null;
        if (record.phase_id && firstLessonDates.has(record.phase_id)) {
          firstLessonDate = firstLessonDates.get(record.phase_id);
        } else if (groupFirstLessonDates.has(record.group_id)) {
          firstLessonDate = groupFirstLessonDates.get(record.group_id);
        }

        // Handle phase information properly
        let phaseName = 'Անհայտ փուլ';
        let phaseNumber = 0;
        
        if (phase) {
          // We have the actual phase from database
          phaseName = phase.name;
          phaseNumber = phase.phase_number;
        } else if (record.phase_id === null && record.group_id) {
          // No phase_id - check if group has phases at all
          const groupPhases = phases.filter(p => p.group_id === record.group_id);
          if (groupPhases.length === 0) {
            // Group has no phases - this is legacy data or group without phase system
            phaseName = 'Ընդհանուր վարձ (փուլեր չկան)';
            phaseNumber = 1;
          } else {
            // Group has phases but this record isn't linked to any - this is an error
            phaseName = 'Չկապված փուլ (պահանջվում է ուղղում)';
            phaseNumber = 0;
          }
        } else {
          // Fallback for other cases
          phaseName = 'Անհայտ փուլ';
          phaseNumber = 0;
        }
        
        console.log(`Record ${record.id}: Phase ID: ${record.phase_id}, Phase: ${phaseName}, Phase Number: ${phaseNumber}`);
        
        return {
          id: record.id,
          studentName: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Անհայտ ուսանող',
          studentId: record.user_id,
          groupName: group?.name || 'Անհայտ խումբ',
          groupId: record.group_id,
          phaseName: phaseName,
          phaseNumber: phaseNumber,
          tuitionAmount: Math.abs(record.amount),
          status: record.status || 'pending',
          paymentDate: firstLessonDate || record.created_at, // Use first lesson date if available
          createdAt: record.created_at
        };
      }) || [];

      // Sort by group name, then by actual phase number from database, then by student name
      tuitionRecords.sort((a, b) => {
        if (a.groupName !== b.groupName) {
          return a.groupName.localeCompare(b.groupName);
        }
        if (a.phaseNumber !== b.phaseNumber) {
          return a.phaseNumber - b.phaseNumber;
        }
        return a.studentName.localeCompare(b.studentName);
      });

      // Group by student and show only latest/most relevant record per student
      const studentGroups = new Map<string, StudentTuitionData[]>();
      tuitionRecords.forEach(record => {
        const key = `${record.studentId}-${record.groupId}`;
        if (!studentGroups.has(key)) {
          studentGroups.set(key, []);
        }
        studentGroups.get(key)!.push(record);
      });

      // Get the most recent unpaid record or the latest record for each student
      const displayRecords: StudentTuitionData[] = [];
      studentGroups.forEach(records => {
        // Find the most recent unpaid record, or if all paid, the most recent one
        const unpaidRecords = records.filter(r => r.status !== 'approved');
        const recordToShow = unpaidRecords.length > 0 
          ? unpaidRecords.sort((a, b) => a.phaseNumber - b.phaseNumber)[0] // Show first unpaid phase
          : records.sort((a, b) => b.phaseNumber - a.phaseNumber)[0]; // Show latest phase if all paid
        
        displayRecords.push(recordToShow);
      });

      // Sort display records by group and student name
      displayRecords.sort((a, b) => {
        if (a.groupName !== b.groupName) {
          return a.groupName.localeCompare(b.groupName);
        }
        return a.studentName.localeCompare(b.studentName);
      });

      setTuitionData(displayRecords);
      // Also store all records for the detail view
      setAllTuitionData(tuitionRecords);
    } catch (error) {
      console.error('Error fetching student tuition data:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց բեռնել ուսանողների վարձավճարի տվյալները",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const regenerateAllTuitionRecords = async () => {
    if (!confirm('Վերահաշվարկե՞լ բոլոր ուսանողների վարձավճարները։ Այս գործողությունը կստեղծի բացակայող վարձավճարային գրառումներ:')) {
      return;
    }

    setLoading(true);
    try {
      // Call the database function to regenerate all tuition records
      const { error } = await supabase.rpc('regenerate_all_student_tuition');
      
      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: "Բոլոր վարձավճարային գրառումները հաջողությամբ վերահաշվարկվեցին"
      });
      await fetchStudentTuitionData();
    } catch (error) {
      console.error('Error regenerating tuition records:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց վերահաշվարկել վարձավճարային գրառումները",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const markTuitionAsPaid = async (data: StudentTuitionData) => {
    try {
      setPayingStudent(data.id);

      const currentTimestamp = new Date().toISOString();

      // First, update the debt record status to approved
      const { error: updateError } = await supabase
        .from('financial_records')
        .update({ 
          status: 'approved',
          approved_at: currentTimestamp
        })
        .eq('id', data.id);

      if (updateError) throw updateError;

      // Create a corresponding payment record to offset the debt
      const { error: paymentError } = await supabase
        .from('financial_records')
        .insert({
          user_id: data.studentId,
          amount: data.tuitionAmount, // Positive amount to offset the negative debt
          type: 'tuition_payment',
          description: `${data.phaseName} - ${data.groupName} (վճարում)`,
          phase_id: null, // Will be set based on original record
          group_id: data.groupId,
          status: 'approved',
          approved_at: currentTimestamp
        });

      if (paymentError) throw paymentError;

      toast({
        title: "Հաջողություն",
        description: `${data.studentName}-ի վարձավճարը վճարված է նշանակվել և հաշվեկշռից հեռացվել`
      });

      // Update local state instead of full refresh
      setTuitionData(prevData => 
        prevData.map(item => 
          item.id === data.id ? { ...item, status: 'approved' } : item
        )
      );
      
      setAllTuitionData(prevData => 
        prevData.map(item => 
          item.id === data.id ? { ...item, status: 'approved' } : item
        )
      );

    } catch (error) {
      console.error('Error marking tuition as paid:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց նշանակել վարձավճարը որպես վճարված",
        variant: "destructive"
      });
    } finally {
      setPayingStudent(null);
    }
  };

  const markTuitionAsUnpaid = async (data: StudentTuitionData) => {
    try {
      setPayingStudent(data.id);

      // Update the debt record status to pending
      const { error: updateError } = await supabase
        .from('financial_records')
        .update({ 
          status: 'pending',
          approved_at: null
        })
        .eq('id', data.id);

      if (updateError) throw updateError;

      // Remove any corresponding payment records for this debt
      const { error: deletePaymentError } = await supabase
        .from('financial_records')
        .delete()
        .eq('user_id', data.studentId)
        .eq('group_id', data.groupId)
        .eq('type', 'tuition_payment')
        .eq('amount', data.tuitionAmount)
        .like('description', `%${data.phaseName}%${data.groupName}%(վճարում)`);

      if (deletePaymentError) {
        console.warn('Warning: Could not delete payment record:', deletePaymentError);
        // Don't throw error here as the main operation (marking as unpaid) succeeded
      }

      toast({
        title: "Հաջողություն",
        description: `${data.studentName}-ի վարձավճարը չվճարված է նշանակվել և հաշվեկշռում վերականգնվել`
      });

      // Update local state instead of full refresh
      setTuitionData(prevData => 
        prevData.map(item => 
          item.id === data.id ? { ...item, status: 'pending' } : item
        )
      );
      
      setAllTuitionData(prevData => 
        prevData.map(item => 
          item.id === data.id ? { ...item, status: 'pending' } : item
        )
      );

    } catch (error) {
      console.error('Error marking tuition as unpaid:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց նշանակել վարձավճարը որպես չվճարված",
        variant: "destructive"
      });
    } finally {
      setPayingStudent(null);
    }
  };

  const deleteTuitionRecord = async (data: StudentTuitionData) => {
    if (!confirm(`Ջնջե՞լ ${data.studentName}-ի ${data.phaseName} փուլի վարձավճարը։`)) {
      return;
    }

    try {
      setPayingStudent(data.id);

      const { error } = await supabase
        .from('financial_records')
        .delete()
        .eq('id', data.id);

      if (error) throw error;

      toast({
        title: "Հաջողություն",
        description: `Վարձավճարային գրառումը ջնջվեց`
      });

      // Update local state instead of full refresh
      setTuitionData(prevData => prevData.filter(item => item.id !== data.id));
      setAllTuitionData(prevData => prevData.filter(item => item.id !== data.id));

    } catch (error) {
      console.error('Error deleting tuition record:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ջնջել վարձավճարային գրառումը",
        variant: "destructive"
      });
    } finally {
      setPayingStudent(null);
    }
  };

  const formatAmount = (amount: number): string => {
    return new Intl.NumberFormat('hy-AM', {
      style: 'currency',
      currency: 'AMD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return formatArmenianDate(dateString);
  };

  const getStudentAllPhases = (studentId: string, groupId: string): StudentTuitionData[] => {
    return allTuitionData.filter(data => 
      data.studentId === studentId && data.groupId === groupId
    ).sort((a, b) => a.phaseNumber - b.phaseNumber);
  };

  useEffect(() => {
    fetchStudentTuitionData();
  }, []);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5" />
            Ուսանողների վարձավճարներ
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Բեռնվում է...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-border/70 bg-card/80">
      <CardHeader className="gap-3 px-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="w-5 h-5" />
          Ուսանողների վարձավճարներ
        </CardTitle>
        <div className="flex w-full gap-2 sm:w-auto">
          <Button 
            onClick={regenerateAllTuitionRecords}
            disabled={loading}
            variant="outline"
            size="sm"
            className="min-h-11 w-full sm:w-auto"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Վերահաշվարկել բոլորը
          </Button>
        </div>
      </CardHeader>
      <CardContent className="px-4 sm:px-6">
        {tuitionData.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-muted-foreground">Վարձավճարի տվյալներ չկան</p>
          </div>
        ) : (
          <div className="-mx-4 overflow-x-auto px-4 [scrollbar-width:thin] sm:mx-0 sm:px-0">
            <Table className="min-w-[900px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Ուսանող</TableHead>
                  <TableHead>Խումբ</TableHead>
                  <TableHead>Փուլ</TableHead>
                  <TableHead>Գումար</TableHead>
                  <TableHead>Կարգավիճակ</TableHead>
                  <TableHead>Վճարման ամսաթիվ</TableHead>
                  <TableHead>Գործողություններ</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tuitionData.map((data) => (
                  <TableRow key={data.id}>
                    <TableCell className="font-medium">
                      {data.studentName}
                    </TableCell>
                    <TableCell>{data.groupName}</TableCell>
                    <TableCell>
                      {data.phaseName}
                      <div className="text-xs text-muted-foreground mt-1">
                        Փուլ #{data.phaseNumber}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">
                      {formatAmount(data.tuitionAmount)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={data.status === 'approved' ? 'default' : 'secondary'}>
                        {data.status === 'approved' ? 'Վճարված' : 'Չվճարված'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {formatDate(data.paymentDate)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {data.status !== 'approved' && (
                          <Button
                            size="sm"
                            onClick={() => markTuitionAsPaid(data)}
                            disabled={payingStudent === data.id}
                            className="h-8"
                          >
                            {payingStudent === data.id ? (
                              <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1" />
                            ) : (
                              <Check className="w-3 h-3 mr-1" />
                            )}
                            Վճարված
                          </Button>
                        )}
                        <Dialog 
                          open={dialogOpen && selectedDialogData?.id === data.id}
                          onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (open) {
                              setSelectedDialogData(data);
                            } else {
                              setSelectedDialogData(null);
                            }
                          }}
                        >
                          <DialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="h-8"
                              onClick={() => {
                                setSelectedDialogData(data);
                                setDialogOpen(true);
                              }}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              Դիտել
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-h-[80vh] w-[calc(100vw-2rem)] max-w-4xl overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle>
                                {selectedDialogData?.studentName} - {selectedDialogData?.groupName}
                              </DialogTitle>
                            </DialogHeader>
                            <div className="space-y-6">
                              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                  <h4 className="font-semibold mb-2">Ընդհանուր տեղեկություններ</h4>
                                  <div className="space-y-2 text-sm">
                                    <div><span className="font-medium">Ուսանող:</span> {selectedDialogData?.studentName}</div>
                                    <div><span className="font-medium">Խումբ:</span> {selectedDialogData?.groupName}</div>
                                    <div><span className="font-medium">Փուլ:</span> {selectedDialogData?.phaseName}</div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="font-semibold mb-2">Վճարման տեղեկություններ</h4>
                                  <div className="space-y-2 text-sm">
                                    <div><span className="font-medium">Գումար:</span> {formatAmount(selectedDialogData?.tuitionAmount || 0)}</div>
                                    <div><span className="font-medium">Կարգավիճակ:</span> 
                                      <Badge variant={selectedDialogData?.status === 'approved' ? 'default' : 'secondary'} className="ml-2">
                                        {selectedDialogData?.status === 'approved' ? 'Վճարված' : 'Չվճարված'}
                                      </Badge>
                                    </div>
                                    <div><span className="font-medium">Ամսաթիվ:</span> {formatDate(selectedDialogData?.paymentDate)}</div>
                                  </div>
                                </div>
                              </div>
                              
                              <div>
                                <h4 className="font-semibold mb-3">Բոլոր փուլերի վարձավճարները</h4>
                                <div className="overflow-x-auto">
                                  <Table className="min-w-[680px]">
                                    <TableHeader>
                                      <TableRow>
                                        <TableHead>Փուլ</TableHead>
                                        <TableHead>Գումար</TableHead>
                                        <TableHead>Կարգավիճակ</TableHead>
                                        <TableHead>Ամսաթիվ</TableHead>
                                        <TableHead>Գործողություններ</TableHead>
                                      </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                      {selectedDialogData && getStudentAllPhases(selectedDialogData.studentId, selectedDialogData.groupId).map((phaseData) => (
                                        <TableRow key={phaseData.id}>
                                          <TableCell>
                                            <div className="font-medium">{phaseData.phaseName}</div>
                                            <div className="text-xs text-muted-foreground">Փուլ #{phaseData.phaseNumber}</div>
                                          </TableCell>
                                          <TableCell className="font-mono">{formatAmount(phaseData.tuitionAmount)}</TableCell>
                                          <TableCell>
                                            <Badge variant={phaseData.status === 'approved' ? 'default' : 'secondary'}>
                                              {phaseData.status === 'approved' ? 'Վճարված' : 'Չվճարված'}
                                            </Badge>
                                          </TableCell>
                                          <TableCell>{formatDate(phaseData.paymentDate)}</TableCell>
                                          <TableCell>
                                            <div className="flex gap-1">
                                              {phaseData.status !== 'approved' ? (
                                                <Button
                                                  size="sm"
                                                  onClick={() => markTuitionAsPaid(phaseData)}
                                                  disabled={payingStudent === phaseData.id}
                                                  className="h-7 px-2"
                                                >
                                                  {payingStudent === phaseData.id ? (
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1" />
                                                  ) : (
                                                    <Check className="w-3 h-3 mr-1" />
                                                  )}
                                                  Վճարված
                                                </Button>
                                              ) : (
                                                <Button
                                                  size="sm"
                                                  variant="outline"
                                                  onClick={() => markTuitionAsUnpaid(phaseData)}
                                                  disabled={payingStudent === phaseData.id}
                                                  className="h-7 px-2"
                                                >
                                                  {payingStudent === phaseData.id ? (
                                                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-border mr-1" />
                                                  ) : (
                                                    <Undo2 className="w-3 h-3 mr-1" />
                                                  )}
                                                  Չեղարկել
                                                </Button>
                                              )}
                                              <Button
                                                size="sm"
                                                variant="destructive"
                                                onClick={() => deleteTuitionRecord(phaseData)}
                                                disabled={payingStudent === phaseData.id}
                                                className="h-7 px-2"
                                              >
                                                {payingStudent === phaseData.id ? (
                                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1" />
                                                ) : (
                                                  <Trash2 className="w-3 h-3 mr-1" />
                                                )}
                                                Ջնջել
                                              </Button>
                                            </div>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </div>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};